import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateBreadcrumbSchema } from '../../utils/seo';

const SERVICES = [
  {
    title: 'Vehicle Registration',
    price: 'Contact for Quote',
    icon: 'ri-car-line',
    link: '/vehicle-registration',
    badge: 'Most Popular',
    desc: 'Complete vehicle registration services for new and used vehicles, renewals, and out-of-state transfers.',
    features: ['New vehicle registration', 'Registration renewals', 'Out-of-state transfers', 'Fleet registrations', 'Motorcycle registration', 'RV and trailer registration'],
    docs: ['Vehicle title or MSO', 'Proof of Nevada insurance', 'Valid ID', 'Smog certificate (if required)'],
    img: 'https://public.readdy.ai/ai/img_res/67152958bb76d2bacfe6d429c479591d.jpg',
  },
  {
    title: 'Title Transfer',
    price: 'Contact for Quote',
    icon: 'ri-file-transfer-line',
    link: '/title-transfer',
    badge: '',
    desc: 'Fast and secure title transfers for private party sales, dealership purchases, and gift transfers.',
    features: ['Private party transfers', 'Dealership sale transfers', 'Gift & inheritance transfers', 'Lien holder changes', 'Name changes on titles', 'Corrected titles'],
    docs: ['Current vehicle title', 'Bill of sale', 'Valid ID for all parties', 'Lien release (if applicable)'],
    img: 'https://readdy.ai/api/search-image?query=Nevada%20vehicle%20title%20transfer%20documents%20official%20seals%20notary%20stamps%20dark%20professional%20desk%20gold%20accent%20lighting%20premium%20office%20environment&width=600&height=350&seq=svc-title-gold-v2&orientation=landscape',
  },
  {
    title: 'License Plates',
    price: 'Contact for Quote',
    icon: 'ri-price-tag-3-line',
    link: '/license-plates',
    badge: '',
    desc: 'Standard, specialty, and personalized Nevada license plates with same-day service available.',
    features: ['Standard Nevada plates', 'Specialty organization plates', 'Personalized custom plates', 'Replacement plates', 'Plate transfers', 'Disability & veteran plates'],
    docs: ['Current registration', 'Valid ID', 'Plate application', 'Payment for fees'],
    img: 'https://readdy.ai/api/search-image?query=Nevada%20license%20plates%20display%20dark%20background%20gold%20lighting%20premium%20professional%20automotive%20DMV%20service%20environment&width=600&height=350&seq=svc-plates-gold-v2&orientation=landscape',
  },
  {
    title: 'Duplicate Title',
    price: 'Contact for Quote',
    icon: 'ri-file-copy-line',
    link: '/duplicate-title',
    badge: '',
    desc: 'Quick replacement for lost, stolen, or damaged vehicle titles with secure processing.',
    features: ['Lost title replacement', 'Stolen title replacement', 'Damaged title replacement', 'Lien release processing', 'Title corrections', 'Expedited processing'],
    docs: ['Valid ID', 'Vehicle information', 'Lien info (if applicable)', 'Police report (stolen titles)'],
    img: 'https://readdy.ai/api/search-image?query=Nevada%20duplicate%20title%20application%20forms%20official%20DMV%20paperwork%20dark%20professional%20desk%20gold%20accent%20lighting%20premium%20office%20environment&width=600&height=350&seq=svc-dup-gold-v2&orientation=landscape',
  },
  {
    title: 'Temporary Permits',
    price: 'Contact for Quote',
    icon: 'ri-time-line',
    link: '/temporary-permits',
    badge: 'Same Day',
    desc: '30-day and 60-day temporary movement permits for unregistered vehicles and special situations.',
    features: ['30-day movement permits', '60-day extended permits', 'Transit tags', 'Dealer temporary permits', 'Trip permits', 'Emergency permits'],
    docs: ['Vehicle identification', 'Proof of ownership', 'Valid ID', 'Insurance proof'],
    img: 'https://readdy.ai/api/search-image?query=Nevada%20temporary%20vehicle%20permits%20movement%20tags%20dark%20professional%20desk%20gold%20accent%20lighting%20official%20DMV%20documentation%20premium%20environment&width=600&height=350&seq=svc-temp-gold-v2&orientation=landscape',
  },
  {
    title: 'Notary Services',
    price: 'Contact for Quote',
    icon: 'ri-quill-pen-line',
    link: '/notary-services',
    badge: '',
    desc: 'Licensed notary public services for all DMV documents and general notarization needs.',
    features: ['Title notarization', 'Bill of sale notarization', 'Affidavit notarization', 'Power of attorney notary', 'Document authentication', 'Signature witnessing'],
    docs: ['Valid government-issued ID', 'Original documents', 'All parties present', 'Understanding of document'],
    img: 'https://readdy.ai/api/search-image?query=professional%20notary%20services%20official%20seal%20stamp%20documents%20dark%20elegant%20desk%20gold%20accent%20lighting%20premium%20office%20environment%20Nevada&width=600&height=350&seq=svc-notary-gold-v2&orientation=landscape',
  },
];

const ADDITIONAL = [
  { icon: 'ri-id-card-line', title: 'ID Renewals', desc: 'Nevada ID card renewals and replacements' },
  { icon: 'ri-bike-line', title: 'Motorcycle Services', desc: 'Motorcycle registration and title services' },
  { icon: 'ri-truck-line', title: 'Commercial Vehicles', desc: 'Commercial vehicle registration and permits' },
  { icon: 'ri-wheelchair-line', title: 'Disability Services', desc: 'Disability plates and parking permits' },
  { icon: 'ri-home-line', title: 'Address Changes', desc: 'Update address on registration and title' },
  { icon: 'ri-team-line', title: 'Fleet Services', desc: 'Multiple vehicle registration services' },
  { icon: 'ri-shield-check-line', title: 'Lien Services', desc: 'Lien additions and releases' },
  { icon: 'ri-speed-line', title: 'Rush Processing', desc: 'Expedited service for urgent needs' },
];

export default function ServicesPage() {
  const [expandedService, setExpandedService] = useState<number | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'DMV Services Las Vegas Nevada - Vehicle Registration, Title Transfer | Speedy DMV',
    description: 'Complete DMV services in Las Vegas Nevada. Vehicle registration, title transfers, license plates, duplicate titles, temporary permits, and notary services. Licensed Nevada DMV provider. Call 702.979.7202.',
    keywords: 'DMV services Las Vegas, vehicle registration Nevada, title transfer Las Vegas, license plates Nevada, duplicate title, temporary permits, notary services Las Vegas',
    canonical: `${siteUrl}/services`,
    schema: { "@context": "https://schema.org", "@graph": [generateLocalBusinessSchema(), generateBreadcrumbSchema([{ name: 'Home', url: '/' }, { name: 'Services', url: '/services' }])] }
  });

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />

      {/* ── HERO ── */}
      <section className="relative pt-[104px]">
        <div className="relative h-[380px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=luxury%20professional%20DMV%20service%20office%20dark%20elegant%20interior%20gold%20accents%20modern%20premium%20business%20environment%20Las%20Vegas%20Nevada%20cinematic%20lighting&width=1920&height=600&seq=services-hero-gold-v2&orientation=landscape"
            alt="DMV Services Las Vegas"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#003366]/82"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <div className="flex items-center space-x-2 text-white/60 text-xs mb-5">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#F5A623]">Services</span>
            </div>
            <div className="inline-flex items-center space-x-2 bg-[#F5A623] text-white text-xs font-bold px-4 py-1.5 rounded mb-4 uppercase tracking-widest">
              <i className="ri-shield-check-line"></i>
              <span>Licensed 3rd Party Nevada DMV Provider</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
              Complete DMV Services <span className="text-[#F5A623]">in Las Vegas</span>
            </h1>
            <p className="text-white/85 text-lg max-w-2xl">
              Licensed, bonded, and experienced — we handle all your Nevada DMV transactions fast and professionally.
            </p>
          </div>
        </div>
      </section>

      {/* ── NOTICE BAR ── */}
      <div className="bg-[#FFF8E7] border-b-2 border-[#F5A623]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center space-x-3">
            <i className="ri-information-line text-[#F5A623] text-lg flex-shrink-0"></i>
            <p className="text-sm text-[#7a5c00]">
              <strong>We are NOT the Nevada DMV.</strong> Speedy DMV Services is a licensed, independent 3rd party provider. We assist with DMV transactions for a service fee.
            </p>
          </div>
        </div>
      </div>

      {/* ── SERVICES GRID ── */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">
            All Nevada DMV Services
          </h2>
          <p className="text-gray-500 mb-10 pl-5">Everything you need — under one roof, without the government wait times.</p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((svc, i) => (
              <div key={i} className={`group border rounded-lg overflow-hidden transition-all hover:shadow-md ${expandedService === i ? 'border-[#003366] shadow-md' : 'border-gray-200'}`}>
                {/* Image header */}
                <div className="relative h-40 overflow-hidden">
                  <img src={svc.img} alt={svc.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-[#003366]/70"></div>
                  <div className="absolute inset-0 flex items-end p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#F5A623] rounded flex-shrink-0">
                        <i className={`${svc.icon} text-white text-base`}></i>
                      </div>
                      <div>
                        <h3 className="font-bold text-white text-base">{svc.title}</h3>
                        <span className="text-[#F5A623] text-xs font-bold">{svc.price}</span>
                      </div>
                    </div>
                  </div>
                  {svc.badge && (
                    <div className="absolute top-3 right-3 bg-[#F5A623] text-white text-xs font-bold px-2 py-0.5 rounded">{svc.badge}</div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5 bg-white">
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{svc.desc}</p>

                  {/* Expandable details */}
                  <button
                    onClick={() => setExpandedService(expandedService === i ? null : i)}
                    className="flex items-center justify-between w-full text-left text-xs font-bold text-[#003366] uppercase tracking-widest mb-3 cursor-pointer hover:text-[#F5A623] transition-colors"
                  >
                    <span>View Details & Documents</span>
                    <i className={`${expandedService === i ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-base`}></i>
                  </button>

                  {expandedService === i && (
                    <div className="grid grid-cols-2 gap-4 mb-4 pt-3 border-t border-gray-100">
                      <div>
                        <p className="text-[#003366] text-xs font-bold uppercase tracking-widest mb-2">Services Include</p>
                        <ul className="space-y-1.5">
                          {svc.features.slice(0, 4).map((f, idx) => (
                            <li key={idx} className="flex items-center space-x-1.5 text-xs text-gray-600">
                              <i className="ri-checkbox-circle-fill text-[#F5A623] flex-shrink-0"></i>
                              <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-[#003366] text-xs font-bold uppercase tracking-widest mb-2">Required Docs</p>
                        <ul className="space-y-1.5">
                          {svc.docs.map((d, idx) => (
                            <li key={idx} className="flex items-center space-x-1.5 text-xs text-gray-600">
                              <i className="ri-file-text-line text-gray-400 flex-shrink-0"></i>
                              <span>{d}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Link to={svc.link} className="flex-1 flex items-center justify-center space-x-1.5 border border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white py-2.5 rounded font-bold text-xs transition-colors whitespace-nowrap">
                      <span>Learn More</span>
                      <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                    <a href="tel:7029797202" className="flex-1 flex items-center justify-center space-x-1.5 bg-[#F5A623] hover:bg-[#e09510] text-white py-2.5 rounded font-bold text-xs transition-colors whitespace-nowrap">
                      <i className="fas fa-phone text-xs"></i>
                      <span>Call Now</span>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ADDITIONAL SERVICES ── */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">
            Additional Services
          </h2>
          <p className="text-gray-500 mb-8 pl-5">We handle more than just the basics.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {ADDITIONAL.map((a, i) => (
              <div key={i} className="group flex items-start space-x-3 p-4 bg-white border border-gray-200 rounded-lg hover:border-[#003366] hover:bg-blue-50/30 transition-all cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center bg-[#003366] rounded flex-shrink-0 group-hover:bg-[#F5A623] transition-colors">
                  <i className={`${a.icon} text-[#F5A623] group-hover:text-white text-base transition-colors`}></i>
                </div>
                <div>
                  <p className="font-bold text-[#003366] text-sm">{a.title}</p>
                  <p className="text-gray-400 text-xs mt-0.5 leading-relaxed">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-14 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">
            Our Simple 4-Step Process
          </h2>
          <p className="text-gray-500 mb-10 pl-5">Most customers are in and out in under 30 minutes.</p>
          <div className="grid md:grid-cols-4 gap-6 relative">
            <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-0.5 bg-[#003366]/15"></div>
            {[
              { step: '01', icon: 'ri-phone-line', title: 'Call Ahead', desc: 'Schedule your appointment and confirm required documents.' },
              { step: '02', icon: 'ri-file-list-3-line', title: 'Bring Documents', desc: 'Gather required paperwork based on your service type.' },
              { step: '03', icon: 'ri-exchange-line', title: 'Fast Processing', desc: 'Our experts handle everything quickly and accurately.' },
              { step: '04', icon: 'ri-check-double-line', title: 'Complete!', desc: 'Receive your documents. Most services completed same day.' },
            ].map((item, i) => (
              <div key={i} className="text-center relative group">
                <div className="w-20 h-20 mx-auto flex items-center justify-center bg-[#003366] rounded-full mb-4 relative z-10 group-hover:bg-[#F5A623] transition-colors">
                  <i className={`${item.icon} text-[#F5A623] group-hover:text-white text-2xl transition-colors`}></i>
                </div>
                <span className="text-[#F5A623] text-xs font-bold tracking-widest uppercase">Step {item.step}</span>
                <h3 className="font-bold text-[#003366] text-base mt-1 mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-14 bg-[#003366]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">Ready to Skip the DMV Lines?</h2>
              <p className="text-white/70 text-sm">Call now to schedule your appointment. Fast, professional Nevada DMV services.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="tel:7029797202" className="flex items-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-7 py-3.5 rounded font-bold text-sm transition-colors whitespace-nowrap">
                <i className="fas fa-phone text-xs"></i>
                <span>702.979.7202</span>
              </a>
              <Link to="/appointment" className="flex items-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-7 py-3.5 rounded font-bold text-sm transition-colors whitespace-nowrap">
                <i className="ri-calendar-check-line"></i>
                <span>Book Appointment</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
